import { motion } from "framer-motion";
import { SchoolListType } from "utils/interfaces";
import { LoadingDots } from "utils/loadingAnimation";
import ImageSkeleton from "utils/skeletons/ImageSkeleton";

interface SchoolsDropdownListProps {
  schoolData: SchoolListType[];
  setShowSchoolListTab: React.Dispatch<React.SetStateAction<boolean>>;
  setContextSchool: React.Dispatch<React.SetStateAction<SchoolListType | null>>;
}

interface TabOptionProps {
  school: SchoolListType;
  handleClickOption: () => void;
}

const TabOption: React.FC<TabOptionProps> = ({ school, handleClickOption }) => {
  return (
    <button
      className="w-full p-3 rounded-lg justify-start text-start items-center gap-2 inline-flex bg-white text-neutral-700 hover:bg-neutral-700 hover:text-white cursor-pointer"
      onClick={handleClickOption}
    >
      <div className="w-6 h-6 min-w-6 min-h-6 bg-white rounded-full overflow-hidden">
        <ImageSkeleton
          src={`/api/imgs/organisations/logo/${school?._id}`}
          alt="school-logo"
          className="w-full h-full select-none relative"
        />
      </div>
      <div className="w-full text-base font-normal font-sans leading-7 overflow-hidden break-keep whitespace-nowrap text-ellipsis select-none">
        {school.name}
      </div>
    </button>
  );
};

const SchoolsDropdownList: React.FC<SchoolsDropdownListProps> = ({
  schoolData,
  setShowSchoolListTab,
  setContextSchool,
}) => {
  /**
   * Function executed when clicking on a school in the school
   * dropdown list
   * @param school
   */
  const handleClickOption = (school: SchoolListType) => {
    return () => {
      // Set the school
      setContextSchool(school);

      // And force the pop up to be closed
      setShowSchoolListTab(false);
    };
  };

  return (
    <>
      <div
        className="fixed top-0 left-0 w-screen h-screen bg-transparent z-20"
        onClick={() => setShowSchoolListTab(false)}
      />
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        className="absolute w-[250px] top-[40px] left-0 p-3 bg-white rounded-lg shadow justify-center items-center z-30"
      >
        <div className="relative w-full h-fit max-h-[300px] flex-col gap-2 inline-flex overflow-y-auto pop-up-scrollbar">
          {schoolData && schoolData.length > 0 ? (
            schoolData.map((school: SchoolListType, index: number) => (
              <TabOption
                key={index}
                school={school}
                handleClickOption={handleClickOption(school)}
              />
            ))
          ) : (
            <div className="w-full h-fit flex justify-center items-center">
              <LoadingDots bgColor="bg-neutral-300" />
            </div>
          )}
        </div>
      </motion.div>
    </>
  );
};

export default SchoolsDropdownList;
